import type { Lesson } from "@core/grades/types";
import { cn } from "@design-system/utils";
import { platformFeaturesAtom } from "@framework/shared/atoms";
import { trans } from "@mongez/localization";
import { Heart } from "lucide-react";
import { useLessonLikeForLesson } from "../hooks/use-lesson-like.hook";

type LessonLikeButtonProps = {
  lesson: Pick<Lesson, "id"> &
    Partial<Pick<Lesson, "isPaid" | "liked" | "likes">>;
  className?: string;
  compact?: boolean;
  inverse?: boolean;
};

export function LessonLikeButton({
  lesson,
  className,
  compact = false,
  inverse = false,
}: LessonLikeButtonProps) {
  const platformFeatures = platformFeaturesAtom.useValue();
  const like = useLessonLikeForLesson(lesson);

  if (platformFeatures.commentsAndRatings !== true) return null;

  return (
    <button
      aria-disabled={!like.canLike}
      aria-label={
        like.liked
          ? trans("grades.removeLessonLike")
          : trans("grades.likeLesson")
      }
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-full border font-black transition",
        compact ? "h-8 min-w-8 px-2 text-[11px]" : "min-h-11 px-4 text-sm",
        like.liked
          ? "border-rose-200 bg-rose-50 text-rose-600 dark:border-rose-900/60 dark:bg-rose-950/50 dark:text-rose-300"
          : inverse
            ? "border-white/30 bg-slate-950/55 text-white hover:border-rose-300 hover:bg-rose-500/90"
            : like.canLike
              ? "border-slate-200 bg-white text-slate-600 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              : "border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-800 dark:bg-slate-900",
        className,
      )}
      disabled={like.isPending}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        like.toggleLike();
      }}
      title={
        like.canLike
          ? trans("grades.likeLesson")
          : trans("grades.purchaseLessonToLike")
      }
      type="button"
    >
      <Heart className={cn("h-4 w-4", like.liked && "fill-current")} />
      <span>{like.likes}</span>
      {!compact ? (
        <span>
          {like.liked
            ? trans("grades.likedLesson")
            : trans("grades.likeLesson")}
        </span>
      ) : null}
    </button>
  );
}
